import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CredentialCard from './components/CredentialCard';
import FilterBar from './components/FilterBar';
import ShareModal from './components/ShareModal';
import WalletStats from './components/WalletStats';
import ExpirationAlerts from './components/ExpirationAlerts';
import AddCredentialModal from './components/AddCredentialModal';

const CredentialWallet = () => {
  const [credentials, setCredentials] = useState([]);
  const [filteredCredentials, setFilteredCredentials] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedIssuer, setSelectedIssuer] = useState('all');
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);

  // Mock credentials data
  const mockCredentials = [
    {
      id: "1",
      title: "COVID-19 Vaccination Certificate",
      type: "vaccination",
      issuer: "City Health Department",
      issueDate: "2024-03-15",
      expirationDate: "2025-03-15",
      status: "verified",
      blockchainVerified: true,
      verificationHash: "0x1a2b3c4d5e6f7g8h9i0j1k2l3m4n5o6p7q8r9s0t1u2v3w4x5y6z7a8b9c0d1e2f",
      issuerLogo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop&crop=center",
      issuerType: "Government Health Authority",
      details: [
        { label: "Vaccine Type", value: "mRNA COVID-19 Vaccine" },
        { label: "Lot Number", value: "ABC123DEF" },
        { label: "Doses", value: "2/2 Complete" },
        { label: "Healthcare Provider", value: "Dr. Sarah Johnson" }
      ]
    },
    {
      id: "2",
      title: "Health Insurance Coverage",
      type: "insurance",
      issuer: "BlueCross BlueShield",
      issueDate: "2024-01-01",
      expirationDate: "2024-12-31",
      status: "verified",
      blockchainVerified: true,
      verificationHash: "0x2b3c4d5e6f7g8h9i0j1k2l3m4n5o6p7q8r9s0t1u2v3w4x5y6z7a8b9c0d1e2f3g",
      issuerLogo: "https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=100&h=100&fit=crop&crop=center",
      issuerType: "Insurance Provider",
      details: [
        { label: "Policy Number", value: "BC-2024-789456" },
        { label: "Coverage Type", value: "Comprehensive Health" },
        { label: "Deductible", value: "$1,500" },
        { label: "Network", value: "Preferred Provider" }
      ]
    },
    {
      id: "3",
      title: "CPR Certification",
      type: "training",
      issuer: "American Red Cross",
      issueDate: "2024-06-10",
      expirationDate: "2025-06-10",
      status: "verified",
      blockchainVerified: false,
      verificationHash: "0x3c4d5e6f7g8h9i0j1k2l3m4n5o6p7q8r9s0t1u2v3w4x5y6z7a8b9c0d1e2f3g4h",
      issuerLogo: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=100&h=100&fit=crop&crop=center",
      issuerType: "Training Organization",
      details: [
        { label: "Certification Level", value: "Adult CPR/AED" },
        { label: "Course Duration", value: "4 hours" },
        { label: "Instructor", value: "Jane Smith, RN" },
        { label: "Certificate ID", value: "ARC-CPR-2024-001" }
      ]
    },
    {
      id: "4",
      title: "Medical License",
      type: "medical",
      issuer: "State Medical Board",
      issueDate: "2020-07-01",
      expirationDate: "2025-07-01",
      status: "verified",
      blockchainVerified: true,
      verificationHash: "0x4d5e6f7g8h9i0j1k2l3m4n5o6p7q8r9s0t1u2v3w4x5y6z7a8b9c0d1e2f3g4h5i",
      issuerLogo: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=100&h=100&fit=crop&crop=center",
      issuerType: "Medical Licensing Board",
      details: [
        { label: "License Number", value: "MD-2020-12345" },
        { label: "Specialty", value: "Internal Medicine" },
        { label: "Board Certified", value: "Yes" },
        { label: "Practice Location", value: "California" }
      ]
    },
    {
      id: "5",
      title: "HIPAA Training Certificate",
      type: "training",
      issuer: "Healthcare Compliance Institute",
      issueDate: "2023-12-01",
      expirationDate: "2024-12-01",
      status: "expired",
      blockchainVerified: false,
      verificationHash: "0x5e6f7g8h9i0j1k2l3m4n5o6p7q8r9s0t1u2v3w4x5y6z7a8b9c0d1e2f3g4h5i6j",
      issuerLogo: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=100&h=100&fit=crop&crop=center",
      issuerType: "Compliance Training Provider",
      details: [
        { label: "Training Type", value: "HIPAA Privacy & Security" },
        { label: "Course Hours", value: "2 hours" },
        { label: "Completion Score", value: "95%" },
        { label: "Certificate ID", value: "HCI-HIPAA-2023-789" }
      ]
    },
    {
      id: "6",
      title: "Professional Liability Insurance",
      type: "insurance",
      issuer: "Medical Protective",
      issueDate: "2024-01-01",
      expirationDate: "2025-01-01",
      status: "pending",
      blockchainVerified: false,
      verificationHash: "0x6f7g8h9i0j1k2l3m4n5o6p7q8r9s0t1u2v3w4x5y6z7a8b9c0d1e2f3g4h5i6j7k",
      issuerLogo: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=100&h=100&fit=crop&crop=center",
      issuerType: "Insurance Provider",
      details: [
        { label: "Policy Number", value: "MP-2024-456789" },
        { label: "Coverage Amount", value: "$2,000,000" },
        { label: "Coverage Type", value: "Professional Liability" },
        { label: "Specialty Coverage", value: "Internal Medicine" }
      ]
    }
  ];

  useEffect(() => {
    setCredentials(mockCredentials);
  }, []);

  useEffect(() => {
    let filtered = credentials;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered?.filter(credential =>
        credential?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        credential?.issuer?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Apply type filter
    if (selectedType !== 'all') {
      filtered = filtered?.filter(credential => credential?.type === selectedType);
    }

    // Apply status filter
    if (selectedStatus !== 'all') {
      filtered = filtered?.filter(credential => credential?.status === selectedStatus);
    }

    // Apply issuer filter
    if (selectedIssuer !== 'all') {
      filtered = filtered?.filter(credential => credential?.issuer === selectedIssuer);
    }

    setFilteredCredentials(filtered);
  }, [credentials, searchQuery, selectedType, selectedStatus, selectedIssuer]);

  const handleShare = (credential) => {
    setSelectedCredential(credential);
    setShareModalOpen(true);
  };

  const handleView = (credential) => {
    // Mock view functionality
    console.log('Viewing credential:', credential);
  };

  const handleRenew = (credential) => {
    // Mock renewal functionality
    console.log('Renewing credential:', credential);
    // In a real app, this would redirect to the issuer's renewal portal
    window.open(`https://${credential?.issuer?.toLowerCase()?.replace(/\s+/g, '')}.com/renew`, '_blank');
  };

  const handleShareSubmit = (shareData) => {
    console.log('Sharing credential with data:', shareData);
    setShareModalOpen(false);
    setSelectedCredential(null);
  };

  const handleAddCredential = (newCredential) => {
    setCredentials(prev => [newCredential, ...prev]);
  };

  const handleDismissAlert = (credentialId) => {
    setDismissedAlerts(prev => [...prev, credentialId]);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedType('all');
    setSelectedStatus('all');
    setSelectedIssuer('all');
  };

  const credentialTypes = [...new Set(credentials.map(c => c.type))];
  const issuers = [...new Set(credentials.map(c => c.issuer))];
  const nonDismissedCredentials = credentials?.filter(c => !dismissedAlerts?.includes(c?.id));

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Credential Wallet - HealthGuard Pro</title>
        <meta name="description" content="Manage your verifiable credentials with DID-based identity management and zero-knowledge proof capabilities" />
      </Helmet>
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Credential Wallet</h1>
              <p className="text-muted-foreground mt-2">
                Manage your verifiable credentials with blockchain security and zero-knowledge privacy
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                iconName="QrCode"
                iconPosition="left"
                onClick={() => window.location.href = '/verification-portal'}
              >
                Scan QR Code
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setAddModalOpen(true)}
              >
                Add Credential
              </Button>
            </div>
          </div>

          {/* Wallet Stats */}
          <WalletStats credentials={credentials} />

          {/* Expiration Alerts */}
          <ExpirationAlerts
            credentials={nonDismissedCredentials}
            onRenew={handleRenew}
            onDismiss={handleDismissAlert}
          />

          {/* Filter Bar */}
          <FilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedType={selectedType}
            onTypeChange={setSelectedType}
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
            selectedIssuer={selectedIssuer}
            onIssuerChange={setSelectedIssuer}
            onClearFilters={handleClearFilters}
            credentialTypes={credentialTypes}
            issuers={issuers}
          />

          {/* Credentials Grid */}
          {filteredCredentials?.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredCredentials?.map((credential) => (
                <CredentialCard
                  key={credential?.id}
                  credential={credential}
                  onShare={handleShare}
                  onView={handleView}
                  onRenew={handleRenew}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="FileText" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">No credentials found</h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery || selectedType !== 'all' || selectedStatus !== 'all' || selectedIssuer !== 'all' ?'Try adjusting your filters to see more credentials.' :'Start by adding your first verifiable credential to your wallet.'}
              </p>
              {(!searchQuery && selectedType === 'all' && selectedStatus === 'all' && selectedIssuer === 'all') && (
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setAddModalOpen(true)}
                >
                  Add Your First Credential
                </Button>
              )}
            </div>
          )}

          {/* Zero-Knowledge Proof Info */}
          <div className="mt-12 bg-accent/10 border border-accent/20 rounded-lg p-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={24} className="text-accent" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Privacy-First Credential Sharing</h3>
                <p className="text-muted-foreground mb-4">
                  Your credentials use advanced zero-knowledge proof technology to enable selective disclosure. 
                  Share only what's necessary while maintaining complete privacy and security.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-sm text-foreground">Age verification without revealing birth date</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-sm text-foreground">Vaccination status without medical details</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-sm text-foreground">Insurance coverage without policy details</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-sm text-foreground">Blockchain-verified authenticity</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Modals */}
      {shareModalOpen && selectedCredential && (
        <ShareModal
          credential={selectedCredential}
          isOpen={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setSelectedCredential(null);
          }}
          onShare={handleShareSubmit}
        />
      )}
      <AddCredentialModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={handleAddCredential}
      />
    </div>
  );
};

export default CredentialWallet;